import { useNavigate } from "@tanstack/react-router"
import type { RowSelectionState } from "@tanstack/react-table"
import type { Dispatch, SetStateAction } from "react"
import { useState } from "react"

import type { GoodsReceiptPallet } from "@/graphql/codegen/graphql"
import { BasicPathType, useAppView } from "@/hooks/useAppView"
import { NotificationType } from "@/schemas/notification"
import { usePutAwayPallets } from "@/screens/WarehouseReceiving/hooks/usePutAwayPallets"
import { formatPutAwaySuccessMessage } from "@/screens/WarehouseReceiving/utils"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UsePutAwayFlowProps {
  goodsReceiptId: string
  filteredPallets: GoodsReceiptPallet[]
  refetch: () => Promise<GoodsReceiptPallet[]>
}

interface UsePutAwayFlowReturn {
  rowSelection: RowSelectionState
  setRowSelection: Dispatch<SetStateAction<RowSelectionState>>
  putAwayModalOpen: boolean
  selectedPallets: GoodsReceiptPallet[]
  selectedPalletIds: string[]
  selectedCount: number
  putAwayLoading: boolean
  handlePutAway: () => void
  handleConfirmPutAway: () => Promise<void>
  handleClosePutAwayModal: () => void
}

export const usePutAwayFlow = ({
  goodsReceiptId,
  filteredPallets,
  refetch,
}: UsePutAwayFlowProps): UsePutAwayFlowReturn => {
  const { getBasicPathFor, isTabletView } = useAppView()
  const navigate = useNavigate()
  const { putAwayPallets, loading: putAwayLoading } = usePutAwayPallets()
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [putAwayModalOpen, setPutAwayModalOpen] = useState(false)

  const selectedCount = Object.values(rowSelection).filter(Boolean).length
  const selectedPallets = filteredPallets.filter((p) => rowSelection[p.goodsReceiptPalletId])
  const selectedPalletIds = selectedPallets.map((p) => p.goodsReceiptPalletId)

  const handlePutAway = () => {
    if (selectedPallets.length === 0) return
    setPutAwayModalOpen(true)
  }

  const handleConfirmPutAway = async () => {
    if (selectedPalletIds.length === 0) return

    const result = await putAwayPallets(selectedPalletIds)

    if (result.success) {
      setPutAwayModalOpen(false)
      setRowSelection({})
      const updatedPallets = await refetch()

      const areAllPalletsStored = updatedPallets.every((pallet) => pallet.isStored)
      const genericSuccessMessage = `${selectedPalletIds.length} ${selectedPalletIds.length === 1 ? "pallet" : "pallets"} successfully stored.`

      if (areAllPalletsStored) {
        const message = areAllPalletsStored ? "All pallets stored." : genericSuccessMessage

        notify({
          type: NotificationType.Success,
          silent: false,
          message,
        })

        if (isTabletView) {
          navigate({
            to: `${getBasicPathFor(BasicPathType.WAREHOUSE_RECEIVING)}/stored`,
          })
        } else {
          navigate({
            to: `${getBasicPathFor(BasicPathType.WAREHOUSE_RECEIVING)}/stored/$goodsReceiptId/built-pallets`,
            params: { goodsReceiptId },
          })
        }
      } else {
        const destination = selectedPallets[0]?.destinationWarehouseShelving
        const hasCompleteDestination =
          destination?.aisleCode && destination?.sectionCode && destination?.binNumber !== undefined

        const message = hasCompleteDestination
          ? formatPutAwaySuccessMessage(selectedPalletIds.length, {
              aisleCode: destination.aisleCode!,
              sectionCode: destination.sectionCode!,
              binNumber: destination.binNumber!,
            })
          : genericSuccessMessage

        notify({
          type: NotificationType.Success,
          silent: false,
          message,
        })
      }
    } else {
      notify({
        type: NotificationType.Error,
        silent: false,
        message: result.error || "Failed to put away pallets. Please try again.",
      })
    }
  }

  const handleClosePutAwayModal = () => {
    setPutAwayModalOpen(false)
  }

  return {
    rowSelection,
    setRowSelection,
    putAwayModalOpen,
    selectedPallets,
    selectedPalletIds,
    selectedCount,
    putAwayLoading,
    handlePutAway,
    handleConfirmPutAway,
    handleClosePutAwayModal,
  }
}
